import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  readonly isDark = signal<boolean>(this.resolveInitial());

  constructor() {
    effect(() => {
      const theme = this.isDark() ? 'dark' : 'light';
      // data-theme — activates [data-theme='dark'] selector block for Material MDC overrides
      this.document.documentElement.setAttribute('data-theme', theme);
      // colorScheme — activates light-dark() CSS function resolution for our --color-* tokens
      this.document.documentElement.style.colorScheme = theme;
    });

    this.subscribeToSystemChanges();
  }

  toggle(): void {
    const next = !this.isDark();
    this.isDark.set(next);
    this.persist(next);
  }

  private resolveInitial(): boolean {
    const win = this.document.defaultView;
    if (!win) return false;

    const stored = this.readStored();
    if (stored !== null) return stored === 'dark';

    return win.matchMedia?.(DARK_QUERY).matches ?? false;
  }

  // Track OS theme changes so users without an explicit override (toggle in dev mode)
  // follow their system preference live — required for prod where the toggle is hidden.
  private subscribeToSystemChanges(): void {
    const mql = this.document.defaultView?.matchMedia?.(DARK_QUERY);
    if (!mql) return;

    mql.addEventListener('change', (event) => {
      if (this.readStored() !== null) return;
      this.isDark.set(event.matches);
    });
  }

  private readStored(): string | null {
    try {
      return this.document.defaultView?.localStorage.getItem(STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  }

  private persist(isDark: boolean): void {
    try {
      this.document.defaultView?.localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
    } catch {
      // localStorage unavailable (private mode, SSR) — silently skip
    }
  }
}
